import { Button } from '@/components/ui/button'
import {
  Alert,
  Fade,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'
import { Modal } from '../../../components'
import { useInputText } from '../../../hooks'
import services from '../../../services'
import { ModalRegisterProps } from './@types'
import { EXPIRATION_OPTIONS } from './expiration-options'

const PACKAGES_QUERY_KEY = ['/bill-packages']

export default function ModalRegister({ open, onClose }: ModalRegisterProps) {
  const queryClient = useQueryClient()
  const initialValues = {
    name: '',
    price: '',
    description: '',
    resourcesText: '',
    expirationDays: '',
  }
  const input = useInputText(initialValues)

  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const closeModal = () => {
    input.clear()
    setError(null)
    onClose()
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault()
      if (error) setError(null)
      setLoading(true)

      const { name, price, description, resourcesText, expirationDays } = input
      const resources = resourcesText
        ? resourcesText
            .split(/[,;\n]/)
            .map((s) => s.trim())
            .filter(Boolean)
        : []
      const expirationDaysNum =
        expirationDays !== '' ? parseInt(expirationDays, 10) : undefined

      await services.packages.create({
        name,
        price: String(price),
        description,
        resources,
        ...(expirationDaysNum != null && expirationDaysNum > 0 && { expirationDays: expirationDaysNum }),
      })

      queryClient.invalidateQueries({ queryKey: PACKAGES_QUERY_KEY })
      toast.success('Pacote criado com sucesso.')
      closeModal()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={closeModal}
      head={<h2 className="text-xl font-bold">Registar novo pacote</h2>}
    >
      <Fade in={open}>
        <div className="text-xl grid gap-12">
          {loading && <Alert severity="warning">A enviar dados...</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          <form onSubmit={handleSubmit} className="grid gap-8">
            <div className="grid gap-4">
              <TextField
                autoFocus
                required
                label="Nome"
                name="name"
                value={input.name}
                onChange={input.onChange}
                onBlur={input.onBlur}
                error={!!input.errors.name}
                helperText={input.errors.name}
                disabled={loading}
              />

              <TextField
                required
                label="Preço (MZN)"
                name="price"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={input.price}
                onChange={input.onChange}
                onBlur={input.onBlur}
                error={!!input.errors.price}
                helperText={input.errors.price}
                disabled={loading}
              />

              <TextField
                required
                label="Descrição"
                name="description"
                multiline
                minRows={2}
                value={input.description}
                onChange={input.onChange}
                onBlur={input.onBlur}
                error={!!input.errors.description}
                helperText={input.errors.description}
                disabled={loading}
              />

              <TextField
                label="Recursos (separados por vírgula ou nova linha)"
                name="resourcesText"
                multiline
                minRows={2}
                placeholder="Ex: Acesso a vídeos, Suporte por email"
                value={input.resourcesText}
                onChange={input.onChange}
                disabled={loading}
              />

              <FormControl fullWidth disabled={loading}>
                <InputLabel id="expiration-days-label">Validade</InputLabel>
                <Select
                  labelId="expiration-days-label"
                  label="Validade"
                  name="expirationDays"
                  value={input.expirationDays}
                  onChange={(e: SelectChangeEvent) =>
                    input.updateInitial('expirationDays', e.target.value)
                  }
                >
                  {EXPIRATION_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || 'default'} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="w-max max-w-full ml-auto mt-4 gap-4 flex">
              <Button
                disabled={loading}
                variant="outline"
                type="button"
                onClick={closeModal}
              >
                Cancelar
              </Button>
              <Button disabled={loading} type="submit">
                Submeter
              </Button>
            </div>
          </form>
        </div>
      </Fade>
    </Modal>
  )
}
